import { equalCaseInsensitively, isBlank, isEmpty, notEmpty, parseString, some } from "@de-xima/xima-common-js-lang";
import {
  $byId,
  allByCssHtml,
  byCssHtml,
  havingClass,
  parentElementsWithSelf,
  toJQuery,
} from "@de-xima/xima-common-js-dom";
import $ from "jquery";
import { getWidgetByVar } from "./Util.ts";

let formTracker: FormTracker | undefined;

// As used by JQuery
const SELECTOR_FORM_FIELDS = "input,select,textarea,keygen";

// Helper form elements whose values are not saved
const SELECTOR_FORM_FIELDS_EXCLUDE = [
  ".ui-picklist-filter", // Input field for searching
  ".ui-selectonemenu-filter", // Input field for searching
  ".ui-handsontable input",
  ".ui-tabs>input", // Active index of <p:tabView/>
  "[name='javax.faces.ViewState']",
  ".ui-datatable>input", // Selected item of a datatable
].join(",");

/**
 * This class keeps track of changes to forms in the current page. This information is used to
 * alert the user in case they have made changes that were not saved yet to the server.
 * This may be done by eg. highlighting the save button with a red background; or
 * by alerting the user when they attempt to navigate to another page.
 *
 * To minimize the required amount of communication with the server (AJAX requests),
 * the checks are performed on the client to the extent that is possible. To make
 * this possible, several steps are necessary.
 *
 * - We need to know which button is the save button. All save buttons should be given
 * the (style) class `fc-save-button`.
 * - Given a save button, we need to know which form or which forms belong to that save button.
 * That is, which data will be saved when the user presses the save button. For a normal page,
 * this is usually the entire context layout. For popup dialogs, we take the dialog as the container.
 * Optionally, a parent of a save button may be given the (style) class `fc-save-container`: then only
 * form elements within that container are considered.
 * - We need to know which forms inside the container are applicable at all. Some forms, such as the action bar
 * form are excluded by default. Add the (style) class `fc-save-form-exclude` to a form to exclude it.
 * - Given a form or a container, we need to know which form elements should be tracked for changes: We first take
 * all form elements matching the `SELECTOR_FORM_FIELDS`, and subtract those form fields that match
 * `SELECTOR_FORM_FIELDS_EXCLUDE`, as well as any form field that is within an element with the class `fc-notrack`.
 * This means you can use the class `fc-notrack` to exclude a particular form field or component manually.
 * - We need to know when the form data was saved to the server. Saving is performed on the server, and
 * data validation is as well. When validation fails, the changes are not saved. Whenever changes are
 * saved on the server, the method
 * `de.xima.fc.gui.model.data.ClientFormTrackerHelper#notifySaveSuccess` should be called, this informs the client
 * that saves were changed.
 * - We also need to know when the form data was reset, eg. by selecting another entity in a list of entities,
 * which discards the data entered for the previous entity. Within a container, there may be at most one
 * HTML element with the (style) class `fc-save-id`. The text it contains is used as a key to check
 * for switches between different entities. The text content is usually the entity's ID, or the name of the plugin.
 * - Data is also reset when a popup dialog is opened, we call the instance method `#openedDialog` after a dialog
 * was opened.
 * - When we detect unsaved changes, the class `fc-unsaved` is added to the container. All save buttons
 * within such elements can then be highlighted via CSS.
 * - We need to know when the user changes the form data. For this, we can use behaviors of the JSF / PrimeFaces
 * components. For each composite component (eg. `xi:inputText`), a `pe:javascript` listener is added that
 * calls the appropriate method of the form tracker. When PrimeFaces components are used directly, the listener
 * must be added manually. This may be avoided by overriding the method `PrimeFaces.widget.BaseWidget.callBehavior`,
 * but this method is only available in PrimeFaces 6.3+.
 * - Sometimes user undo their changes to a form; in this case, we would like to detect that the form is indeed
 * unchanged. We save the form data in serialized form via `JQuery#serialize` when the page is opened initially
 * and when it is saved or reset; this enables us to compare it with the current form data.
 * - For the list of files, there is no form data present on the client when the file is binary, eg. an image. Thus
 * we add an additional input field that contains the binary data, or rather its hash code. This allows us to
 * detect changes when a different image was uploaded. This input gets the (style) class `fc-save-content`.
 */
export class FormTracker {
  /**
   * The most recently saved form data for each container.
   */
  private _containerFormData: Record<string, Record<string, string>>;
  private _unsavedChangesHandledCallback: (() => void) | undefined;

  constructor() {
    this._containerFormData = {};
  }

  /**
   * Called after an AJAX was performed that possibly changed the page's contents.
   */
  afterAjax(): void {
    $(() => {
      for (const btn of $(".fc-save-button")) {
        const container = this.findContainer($(btn));
        this.checkFormChanges(container);
      }
    });
  }

  /**
   * @param element Form field that was changed.
   * @param eventName Name of the event that caused the change, eg. "change", "input" etc.
   */
  changed(element: JQuery): void {
    const container = this.findContainer(element);
    if (this._isObserved(container)) {
      this.markChanged(container);
      this.checkFormChanges(container);
    }
  }

  changedAjaxOnComplete(cfg: PrimeFaces.ajax.Configuration): void {
    const element = typeof cfg.source === "string" ? $byId(cfg.source) : toJQuery(cfg.source);
    this.changed(element);
  }

  changedAjaxOnStart(cfg: PrimeFaces.ajax.Configuration): void {
    const element = typeof cfg.source === "string" ? $byId(cfg.source) : toJQuery(cfg.source);
    this.changed(element);
  }

  changedEvent(element: HTMLElement): void {
    this.changed($(element));
  }

  changedFileUpload(widget: PrimeFaces.widget.FileUpload): void {
    this.changed(widget.getJQ());
  }

  changedPeJavascript(clientId: string): void {
    const element = $byId(clientId);
    this.changed(element);
  }

  /**
   * Called after any AJAX request. Checks whether the form data was reset, such as by changing to a different
   * item in a list of editable items. This is called by the composite component `<xi:saveId/>` and should not
   * be used manually.
   * @param elementId ID of the `<xi:saveID/>` element.
   */
  checkForReset(elementId: string): void {
    $(() => {
      const element = $byId(elementId);
      const currentSaveId = this.findSaveId(element);
      if (isEmpty(currentSaveId)) {
        return;
      }
      const container = this.findContainer(element);
      const oldSaveId = parseString(container.data("saveId"));
      container.data("saveId", currentSaveId);
      if (currentSaveId !== oldSaveId) {
        this.reset(element);
      }
    });
  }

  checkFormChanges(container: JQuery): void {
    $(() => {
      const containerId = container.attr("id");
      if (notEmpty(containerId)) {
        const newData = this.findFormData(container);
        const oldData = this._containerFormData[containerId] ?? newData;
        if (this.isFormDataEqual(oldData, newData)) {
          this.markUnchanged(container);
        } else {
          this.markChanged(container);
        }
      }
    });
  }

  /**
   * Finds the container of the given form element for the logical form. The logical form may consist
   * of multiple form HTML elements, but should contain only one save button.
   * @param element Child element to process.
   * @returns Form container of the element.
   */
  findContainer(element: JQuery): JQuery {
    // For confirm dialogs, use entire page
    const base = $(element).closest(".ui-confirm-dialog").length > 0 ? $(".layout-content-wrapper") : element;
    // Check if we were passed the container
    if (base.is(".ui-dialog,.fc-save-container,.layout-content-wrapper")) {
      return base;
    }
    // Buttons do not have to be in a form
    const form = base.closest("form,.fc-save-button");
    // Element not part of a form, ignore.
    if (form.length === 0) {
      return $();
    }
    // Find the current context, dialog, custom, or main UI
    const container = base.closest(".ui-dialog,.fc-save-container,.layout-content-wrapper");
    if (container.length > 0) {
      return container;
    }
    // Default to content
    return $(".layout-content-wrapper");
  }

  findFormData(container: JQuery): Record<string, string> {
    const result: Record<string, string> = {};
    if (container.hasClass("fc-save-container")) {
      return {
        [container.attr("id") ?? ""]: this.serializeApplicableFormField(container),
      };
    }

    // If the container is part of a form, take all elements inside the container.
    // Otherwise, search for all forms.
    const containers = container.closest("form").length > 0 ? container : container.find("form").addBack("form");
    containers
      .not(".fc-actionbar-form,.fc-header-form,.fc-message-form,.fc-save-form-exclude")
      .filter((_idx, form) => {
        return $(form).closest(".context-content-left").length === 0;
      })
      .each((_idx, form) => {
        const data = this.serializeApplicableFormField($(form));
        result[form.id] = data;
      });
    return result;
  }

  findSaveButton(container: JQuery): JQuery {
    const saveButton = container.find(".fc-save-button");
    if (saveButton.length > 0) {
      return saveButton;
    }
    return $();
  }

  findSaveId(container: JQuery): string {
    if (container.hasClass("fc-save-id-val")) {
      return container.text() ?? "";
    }
    return container.find(".fc-save-id-val").text() ?? "";
  }

  init(context: JQuery): void {
    for (const btn of context.find(".fc-save-button")) {
      const container = this.findContainer($(btn));
      const containerId = container.attr("id");
      if (notEmpty(containerId)) {
        $(() => this.reset(container));
      }
    }
    this.initUnsavedChangesDialog(context.get(0));
  }

  initUnsavedChangesDialog(context: HTMLElement = document as unknown as HTMLElement): void {
    const unsavedChangesDialog = byCssHtml(".fc-unsaved-dialog", context);
    if (unsavedChangesDialog === undefined) {
      return;
    }
    for (const element of allByCssHtml("a, .fc-state-changing")) {
      if (FormTracker.shouldHandleUnsavedChanges(element)) {
        element.addEventListener("click", (event) => {
          this.handleDocumentHasChanged(event, element, context);
        });
      }
    }
  }

  handleDocumentHasChanged(
    event?: MouseEvent,
    element?: HTMLElement,
    context: HTMLElement = document as unknown as HTMLElement,
  ): boolean {
    if (this._unsavedChangesHandledCallback) {
      this._unsavedChangesHandledCallback = undefined;
      return true;
    }
    if (this._documentHasUnsavedChanges(context)) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (element && event) {
        this._unsavedChangesHandledCallback =
          event.type === "click" ? () => element.click() : () => $(element).trigger(event.type);
      } else {
        this._unsavedChangesHandledCallback = () => {
          this.resetAllContainer(context);
        };
      }
      getWidgetByVar("unsavedChangesDialog", PrimeFaces.widget.Dialog)?.show();
      return false;
    }
    return true;
  }

  unsavedChangesHandled(): void {
    if (this._unsavedChangesHandledCallback) {
      this._unsavedChangesHandledCallback();
    }
  }

  unsavedChangesHandlingAborted(): void {
    this._unsavedChangesHandledCallback = undefined;
  }

  isFormDataEqual(data1: Record<string, string>, data2: Record<string, string>): boolean {
    const keys1 = Object.keys(data1);
    const keys2 = Object.keys(data2);
    if (keys1.length !== keys2.length) {
      return false;
    }
    for (const key of keys1) {
      if (!(key in data2)) {
        return false;
      }
      if (data1[key] !== data2[key]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Marks the given container or form as having unsaved changes. This adds a style class, so that all save button
   * within the container can be styled appropriately (eg. by turning red)
   * @param container Container to be marked as unsaved.
   */
  markChanged(container: JQuery): void {
    container.addClass("fc-unsaved");
    $(document).trigger("changed.form");
  }

  /**
   * Marks the given container or form as not having any unsaved changed.
   * @param container Container to be marked as saved.
   */
  markUnchanged(container: JQuery): void {
    container.removeClass("fc-unsaved");
    $(document).trigger("unchanged.form");
  }

  onDocumentReady(): void {
    this.init($(document as unknown as HTMLElement));
  }

  openedDialog(clientId: string): void {
    // Delay to make sure dialog is there.
    $(() => {
      const element = $byId(clientId);
      this.init(element);
    });
  }

  /**
   * @param element Element that triggered the reset.
   * @param eventName Name of the event that caused the reset, eg. "rowSelect" etc.
   */
  reset(element: JQuery): void {
    const container = this.findContainer(element);
    if (this._isObserved(container)) {
      this.markUnchanged(container);
      this.saveFormData(container);
    }
  }

  resetAjaxOnStart(cfg: PrimeFaces.ajax.Configuration): void {
    const element = typeof cfg.source === "string" ? $byId(cfg.source) : toJQuery(cfg.source);
    this.reset(element);
  }

  resetPeJavascript(clientId: string): void {
    const element = $byId(clientId);
    this.reset(element);
  }

  saveFormData(container: JQuery): void {
    const containerId = container.attr("id");
    if (notEmpty(containerId)) {
      const data = this.findFormData(container);
      this._containerFormData[containerId] = data;
    }
  }

  saveFormDataEditList(clientId: string): void {
    const cardId = clientId.split(":").slice(0, -4).join(":");
    const container = $byId(`${cardId}:editlist`);
    const expanded = container.find(".fc-el-expanded ");
    this.saveFormData(expanded);
  }

  /**
   * Triggered when the form data was saved.
   * @param elementId ID of the element that triggered the save.
   */
  saved(elementId: string): void {
    const element = $byId(elementId);
    const resolvedElement = element.length > 0 ? element : $(".layout-content-wrapper");
    const container = this.findContainer(resolvedElement);
    this.markUnchanged(container);
    this.saveFormData(container);
  }

  /**
   * For a given container, finds all form fields whose state contributes to whether
   * the user needs to save; and serializes that data. When the data changes, the
   * save button is highlighted to inform the user about unsaved changes.
   * @param container Container with elements to serialize.
   * @returns The serialized form data.
   */
  serializeApplicableFormField(container: JQuery): string {
    const fields = container
      .find(SELECTOR_FORM_FIELDS)
      .filter((_idx, element) => $(element).closest(".fc-notrack").length === 0)
      .not(SELECTOR_FORM_FIELDS_EXCLUDE)
      .serializeArray();
    for (const field of fields) {
      field.value = field.value.trim();
    }
    return $.param(fields);
  }

  resetAllContainer(context: HTMLElement = document as unknown as HTMLElement): void {
    for (const btn of context.getElementsByClassName("fc-save-button")) {
      const container = btn instanceof HTMLElement ? this.findContainer($(btn)) : undefined;
      if (container) {
        this.reset(container);
      }
    }
  }

  private _isObserved(container: JQuery): boolean {
    return this.findSaveButton(container).length > 0;
  }

  private _documentHasUnsavedChanges(context: HTMLElement): boolean {
    for (const btn of context.getElementsByClassName("fc-save-button")) {
      const container = btn instanceof HTMLElement ? this.findContainer($(btn)) : undefined;
      if (container && $(container).hasClass("fc-unsaved")) {
        return true;
      }
    }
    return false;
  }

  private static shouldHandleUnsavedChanges(element: HTMLElement) {
    if (element.classList.contains("fc-state-changing")) {
      return true;
    }
    if (some(parentElementsWithSelf(element), havingClass("fc-state-changing-exclude"))) {
      return false;
    }
    if (element instanceof HTMLAnchorElement) {
      if (isBlank(element.href)) {
        return false;
      }
      const href = new URL(element.href, document.location.href);
      if (notEmpty(href.hash) || element.getAttribute("href")?.startsWith("#")) {
        return false;
      }
      return isBlank(element.target) || equalCaseInsensitively(element.target, "_self", "en");
    }
    return false;
  }

  static getInstance(): FormTracker {
    if (formTracker === undefined) {
      formTracker = new FormTracker();
    }
    return formTracker;
  }
}
